import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';

// Image size options for thumbnailing
export type ImageSize = 'thumb' | 'small' | 'medium' | 'large' | 'full';

interface UseAuthenticatedImageResult {
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuthenticatedImage = (
  classId: string,
  filename: string,
  size: ImageSize = 'medium'
): UseAuthenticatedImageResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't load if classId or filename is empty
    if (!classId || !filename) {
      setLoading(false);
      setBlobUrl(null);
      setError(null);
      return;
    }

    let isMounted = true;
    let currentBlobUrl: string | null = null;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(null);

        const blob = await apiService.getEnrollmentImage(classId, filename, size);

        if (isMounted) {
          currentBlobUrl = URL.createObjectURL(blob);
          setBlobUrl(currentBlobUrl);
        }
      } catch (err: any) {
        console.error(`Failed to load authenticated image: ${classId}/${filename}`, err);
        if (isMounted) {
          setError(err.message || 'Failed to load image');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [classId, filename, size]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return { blobUrl, loading, error };
};